import React, { useState, useEffect } from 'react';

// Main App component for the Space Quiz
const App = () => {
  // State to manage the current question index
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  // State to manage the user's score
  const [score, setScore] = useState(0);
  // State to track if an answer has been selected for the current question
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  // State to track if the quiz has finished
  const [quizFinished, setQuizFinished] = useState(false);
  // State to store the quiz questions, dynamically generated
  const [questions, setQuestions] = useState([]);
  // State to manage loading status for generating questions
  const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);
  // State to manage loading status for explaining answers
  const [isLoadingExplanation, setIsLoadingExplanation] = useState(false);
  // State to store the explanation for the current question
  const [explanation, setExplanation] = useState('');
  // State to control the visibility of the explanation modal
  const [showExplanationModal, setShowExplanationModal] = useState(false);
  // State to control whether the quiz has started (after questions are generated)
  const [quizStarted, setQuizStarted] = useState(false);
  // State to store the number of questions the user wants to generate
  const [numQuestionsToGenerate, setNumQuestionsToGenerate] = useState(5); // Default to 5 questions
  // State for the timer countdown
  const [timer, setTimer] = useState(15); // 15 seconds per question
  // State to track if the timer has run out for the current question
  const [timerExpired, setTimerExpired] = useState(false);

  // Get the current question based on the currentQuestionIndex
  const currentQuestion = questions[currentQuestionIndex];

  // Effect hook to manage the question timer
  useEffect(() => {
    let interval;
    // Only start timer if quiz has started, is not finished, and questions exist
    if (quizStarted && !quizFinished && questions.length > 0) {
      setTimer(15); // Reset timer for new question
      setTimerExpired(false); // Reset timer expired status
      setSelectedAnswer(null); // Clear selected answer for new question

      interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(interval);
            setTimerExpired(true); // Mark timer as expired
            // Automatically move to the next question if timer runs out
            handleNextQuestion(true); // Pass true to indicate timeout
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }

    // Cleanup function to clear the interval when component unmounts or dependencies change
    return () => clearInterval(interval);
  }, [currentQuestionIndex, quizStarted, quizFinished, questions.length]); // Dependencies for useEffect

  // Function to handle answer selection
  const handleAnswerSelect = (option) => {
    // If an answer has already been selected or timer expired, do nothing
    if (selectedAnswer !== null || timerExpired) {
      return;
    }
    setSelectedAnswer(option);
    setExplanation(''); // Clear previous explanation
    setShowExplanationModal(false); // Hide explanation modal
    setTimerExpired(false); // Ensure this is false if user answers before timeout
  };

  // Function to handle moving to the next question
  // `isTimedOut` parameter is true if called due to timer expiry
  const handleNextQuestion = (isTimedOut = false) => {
    // Only update score if an answer was selected and it's correct, and not timed out
    if (!isTimedOut && selectedAnswer === currentQuestion.correctAnswer) {
      setScore(score + 1);
    }

    // Reset states for the next question
    setSelectedAnswer(null);
    setExplanation('');
    setShowExplanationModal(false);
    setTimer(15); // Reset timer for the next question
    setTimerExpired(false); // Reset timer expired status

    // If there are more questions, move to the next one
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // If all questions are answered, set quizFinished to true
      setQuizFinished(true);
      // IMPORTANT: Do NOT set quizStarted to false here.
      // We want to remain on the quiz completion screen.
    }
  };

  // Function to generate the specified number of space quiz questions using Gemini API
  const startQuizWithGeneratedQuestions = async () => {
    setIsLoadingQuestion(true);
    setQuizStarted(false); // Ensure quiz doesn't start until questions are ready
    setQuestions([]); // Clear any previous questions
    setScore(0); // Reset score
    setCurrentQuestionIndex(0); // Reset question index
    setQuizFinished(false); // Reset quiz finished state
    setSelectedAnswer(null); // Reset selected answer
    setExplanation(''); // Clear explanation
    setShowExplanationModal(false); // Hide explanation modal
    setTimer(15); // Reset timer
    setTimerExpired(false); // Reset timer expired status

    const generatedQuestions = [];
    const generatedQuestionTexts = new Set(); // Keep track of generated question texts to avoid duplicates

    // Maximum retries for generating a unique question
    const MAX_RETRIES = 3;

    for (let i = 0; i < numQuestionsToGenerate; i++) {
      let newQuestion = null;
      let retries = 0;

      while (newQuestion === null && retries < MAX_RETRIES) {
        try {
          // Include previously generated questions in the prompt to guide the model
          let prompt = "Generate a multiple-choice space quiz question with 4 options and one correct answer. Provide the output in a JSON format like this: {\"question\": \"Your question?\", \"options\": [\"Option A\", \"Option B\", \"Option C\", \"Option D\"], \"correctAnswer\": \"Correct Option\"}. Ensure the question and options are unique and not easily guessable.";

          if (generatedQuestionTexts.size > 0) {
            prompt += ` Do not generate questions similar to these: ${Array.from(generatedQuestionTexts).join('; ')}.`;
          }

          let chatHistory = [];
          chatHistory.push({ role: "user", parts: [{ text: prompt }] });
          const payload = {
            contents: chatHistory,
            generationConfig: {
              responseMimeType: "application/json",
              responseSchema: {
                type: "OBJECT",
                properties: {
                  "question": { "type": "STRING" },
                  "options": {
                    "type": "ARRAY",
                    "items": { "type": "STRING" }
                  },
                  "correctAnswer": { "type": "STRING" }
                },
                "propertyOrdering": ["question", "options", "correctAnswer"]
              }
            }
          }
          const apiKey = ""; // If you want to use models other than gemini-2.0-flash or imagen-3.0-generate-002, provide an API key here. Otherwise, leave this as-is.
          const apiUrl = `https://tvedbqywp5ruobzfyp4mocymoi0fsuvm.lambda-url.us-east-1.on.aws/${apiKey}`;
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          const result = await response.json();

          if (result.candidates && result.candidates.length > 0 &&
            result.candidates[0].content && result.candidates[0].content.parts &&
            result.candidates[0].content.parts.length > 0) {
            const json = result.candidates[0].content.parts[0].text;
            const potentialQuestion = JSON.parse(json);

            // Check for uniqueness before adding
            if (!generatedQuestionTexts.has(potentialQuestion.question)) {
              newQuestion = potentialQuestion;
              generatedQuestionTexts.add(newQuestion.question); // Add to set for future checks
            } else {
              console.log("Duplicate question generated, retrying...");
              retries++;
            }
          } else {
            console.error(`Failed to generate question (API response issue) for attempt ${retries + 1}:`, result);
            retries++;
          }
        } catch (error) {
          console.error(`Error generating question for attempt ${retries + 1}:`, error);
          retries++;
        }
      }

      if (newQuestion) {
        generatedQuestions.push(newQuestion);
      } else {
        console.warn(`Could not generate a unique question after ${MAX_RETRIES} retries for question ${i + 1}. Skipping.`);
      }
    }

    if (generatedQuestions.length > 0) {
      setQuestions(generatedQuestions);
      setQuizStarted(true); // Start the quiz only if questions were generated
    } else {
      setExplanation("Failed to generate any questions. Please try again with a different number.");
      setShowExplanationModal(true);
    }
    setIsLoadingQuestion(false);
  };

  // Function to explain the current answer using Gemini API
  const explainCurrentAnswer = async () => {
    setIsLoadingExplanation(true);
    try {
      const prompt = `Explain why "${currentQuestion.correctAnswer}" is the correct answer for the question "${currentQuestion.question}". Keep the explanation concise and informative.`;
      let chatHistory = [];
      chatHistory.push({ role: "user", parts: [{ text: prompt }] });
      const payload = { contents: chatHistory };
      const apiKey = ""; // If you want to use models other than gemini-2.0-flash or imagen-3.0-generate-002, provide an API key here. Otherwise, leave this as-is.
      const apiUrl = `https://tvedbqywp5ruobzfyp4mocymoi0fsuvm.lambda-url.us-east-1.on.aws/${apiKey}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();

      if (result.candidates && result.candidates.length > 0 &&
        result.candidates[0].content && result.candidates[0].content.parts &&
        result.candidates[0].content.parts.length > 0) {
        setExplanation(result.candidates[0].content.parts[0].text);
        setShowExplanationModal(true);
      } else {
        console.error("Failed to get explanation: Unexpected API response structure", result);
        setExplanation("Failed to get an explanation. Please try again.");
        setShowExplanationModal(true);
      }
    } catch (error) {
      console.error("Error explaining answer:", error);
      setExplanation("Error getting explanation. Please check your network connection.");
      setShowExplanationModal(true);
    } finally {
      setIsLoadingExplanation(false);
    }
  };

  // Render the quiz content
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 to-purple-900 flex items-center justify-center p-4 font-sans">
      <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-lg rounded-2xl shadow-xl p-8 max-w-md w-full border border-white border-opacity-20">
        <h1 className="text-4xl font-extrabold text-white text-center mb-8 tracking-wide">
          Space Quiz
        </h1>

        {!quizStarted && !quizFinished ? ( // Show initial screen if quiz not started and not finished
          // Initial screen to ask for number of questions
          <div className="text-center">
            <p className="text-xl text-white mb-4">How many questions would you like?</p>
            <input
              type="number"
              min="1"
              max="10" // Limit to a reasonable number for quick generation
              value={numQuestionsToGenerate}
              onChange={(e) => setNumQuestionsToGenerate(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-24 p-2 rounded-lg text-center bg-white bg-opacity-30 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-6"
            />
            <button
              onClick={startQuizWithGeneratedQuestions}
              disabled={isLoadingQuestion || numQuestionsToGenerate < 1}
              className="block w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full shadow-lg transition duration-300 ease-in-out transform hover:scale-105"
            >
              {isLoadingQuestion ? 'Generating Questions...' : 'Start Quiz'}
            </button>
            {isLoadingQuestion && (
              <p className="text-white mt-4">This might take a moment...</p>
            )}
          </div>
        ) : quizFinished ? (
          // Display final score and review when the quiz is finished
          <div className="text-center">
            <p className="text-2xl text-white mb-4">Quiz Completed!</p>
            <p className="text-3xl font-bold text-yellow-300 mb-8">
              Your Score: {score} / {questions.length}
            </p>

            <div className="text-left mb-8 max-h-60 overflow-y-auto pr-2">
              <h3 className="text-xl font-bold text-white mb-4">Review Your Answers:</h3>
              {questions.map((q, index) => (
                <div key={index} className="mb-4 p-3 rounded-lg bg-white bg-opacity-15">
                  <p className="text-white font-semibold mb-1">
                    {index + 1}. {q.question}
                  </p>
                  <p className="text-green-300 text-sm">
                    Correct Answer: {q.correctAnswer}
                  </p>
                </div>
              ))}
            </div>

            <button
              onClick={() => {
                // Reset quiz to go back to the initial question input screen
                setQuizStarted(false);
                setQuizFinished(false);
                setQuestions([]); // Clear questions
                setScore(0);
                setCurrentQuestionIndex(0);
                setSelectedAnswer(null);
                setExplanation('');
                setShowExplanationModal(false);
                setTimer(15); // Reset timer
                setTimerExpired(false); // Reset timer expired status
              }}
              className="mt-8 mx-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full shadow-lg transition duration-300 ease-in-out transform hover:scale-105"
            >
              Play Again
            </button>
          </div>
        ) : (
          // Display current question and options
          <div>
            <p className="text-lg text-white mb-2">
              Question {currentQuestionIndex + 1} of {questions.length}
            </p>
            <div className="text-xl font-bold text-yellow-300 mb-4 text-center">
              Time Left: {timer}s
            </div>
            <h2 className="text-2xl font-semibold text-white mb-6">
              {currentQuestion?.question}
            </h2>
            <div className="space-y-4">
              {currentQuestion?.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(option)}
                  className={`
                    w-full text-left px-5 py-3 rounded-xl transition duration-300 ease-in-out
                    ${selectedAnswer === option
                      ? (option === currentQuestion.correctAnswer
                        ? 'bg-green-500 text-white shadow-md'
                        : 'bg-red-500 text-white shadow-md')
                      : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
                    }
                    ${selectedAnswer !== null && option !== selectedAnswer && option === currentQuestion.correctAnswer
                        ? 'border-2 border-green-400' // Highlight correct answer if wrong one was selected
                        : ''
                    }
                    ${selectedAnswer !== null || timerExpired ? 'cursor-not-allowed' : 'cursor-pointer'}
                  `}
                  disabled={selectedAnswer !== null || timerExpired} // Disable buttons after selection or if timer expired
                >
                  {option}
                </button>
              ))}
            </div>
            <div className="mt-8 text-center flex justify-center space-x-4">
              {selectedAnswer !== null || timerExpired ? ( // Show buttons if answer selected or timer expired
                <>
                  <button
                    onClick={() => handleNextQuestion()} // No `isTimedOut` needed here, as it's explicit user action
                    className="px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-full shadow-lg transition duration-300 ease-in-out transform hover:scale-105"
                  >
                    {currentQuestionIndex < questions.length - 1 ? "Next Question" : "Finish Quiz"}
                  </button>
                  {selectedAnswer !== null && ( // Only show explain button if an answer was actually selected by user
                    <button
                      onClick={explainCurrentAnswer}
                      disabled={isLoadingExplanation}
                      className="px-8 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-full shadow-lg transition duration-300 ease-in-out transform hover:scale-105"
                    >
                      {isLoadingExplanation ? 'Explaining...' : 'Explain Answer ✨'}
                    </button>
                  )}
                </>
              ) : null}
            </div>
          </div>
        )}

        {/* Explanation Modal */}
        {showExplanationModal && explanation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm w-full relative">
              <h3 className="text-xl font-bold mb-4 text-gray-800">Explanation</h3>
              <p className="text-gray-700 mb-4">{explanation}</p>
              <button
                onClick={() => setShowExplanationModal(false)}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                &times;
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
